/**
 * Opens a print/export document in a new tab.
 *
 * Returns false when the browser blocked the popup (`window.open` → null) so
 * callers can react — most importantly, the commitment flow must never mark a
 * scenario "committed" for a document that was never produced.
 */
export function openPrintWindow(html: string): boolean {
  const win = window.open("", "_blank");
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  return true;
}

/**
 * Prints an HTML document from a hidden same-page iframe — no popup, so no
 * popup blocker can interfere. Resolves true once the print dialog was
 * invoked; the browser's "Save as PDF" destination produces a vector PDF.
 *
 * `title` is temporarily set as the page title because browsers derive the
 * suggested print/PDF filename from it.
 */
export function printHtmlInPage(html: string, title?: string): Promise<boolean> {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "1px";
    iframe.style.height = "1px";
    iframe.style.border = "0";
    iframe.style.visibility = "hidden";

    const previousTitle = document.title;
    let settled = false;

    const cleanup = (ok: boolean) => {
      if (settled) return;
      settled = true;
      document.title = previousTitle;
      // Defensive: if a modal's scroll lock was interrupted by the blocking
      // print dialog, make sure the page is interactive again.
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
      // Keep the iframe alive until the print job finishes — Chrome cancels
      // an in-flight print if its frame is removed immediately — then remove
      // it promptly so it never lingers on the page.
      const win = iframe.contentWindow;
      let removed = false;
      const removeIframe = () => {
        if (removed) return;
        removed = true;
        iframe.remove();
      };
      if (win) {
        win.addEventListener("afterprint", removeIframe, { once: true });
      }
      window.setTimeout(removeIframe, 10_000);
      resolve(ok);
    };

    iframe.onload = () => {
      try {
        if (title) document.title = title;
        const win = iframe.contentWindow;
        if (!win) {
          cleanup(false);
          return;
        }
        win.focus();
        // Defer the blocking print() call one tick so any in-flight React
        // state updates (e.g. the report dialog closing) and their effect
        // cleanups commit before the print dialog freezes the main thread.
        // Without this, the dialog's page lock can be left half-removed and
        // the page appears frozen afterwards.
        window.setTimeout(() => {
          try {
            win.print();
            cleanup(true);
          } catch (e) {
            console.warn("iframe print failed", e);
            cleanup(false);
          }
        }, 0);
      } catch (e) {
        console.warn("iframe print failed", e);
        cleanup(false);
      }
    };

    iframe.srcdoc = html;
    document.body.appendChild(iframe);
    // Safety net: if load never fires, fail instead of hanging the caller.
    window.setTimeout(() => cleanup(false), 10_000);
  });
}
